import { Form, Input, Modal } from 'antd';
import { Dispatch, SetStateAction, useState } from 'react';
import { HiOutlineMail, HiOutlineUserAdd } from 'react-icons/hi';
import styled from 'styled-components';

import { User } from '../../../operations/auth/types';
import { sendInvite } from '../../../operations/users';
import { InvitedUser } from '../../../operations/users/types';
import * as inviteUserRules from '../../../rules/invite-user';

const ModalContent = styled.div`
  padding: 8px 0;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  border-radius: 12px;
  margin-bottom: 16px;

  svg {
    width: 24px;
    height: 24px;
    color: var(--primary);
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const InputWrapper = styled.div`
  position: relative;

  .input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(0, 0, 0, 0.25);
    z-index: 1;
    pointer-events: none;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .ant-input {
    padding-left: 36px;
    height: 44px;
    border-radius: 8px;
    font-size: 14px;

    &:focus,
    &:hover {
      border-color: var(--primary);
    }

    &:focus {
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 15%, transparent);
    }
  }
`;

const FooterButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $primary }) =>
    $primary
      ? `
    background: var(--primary);
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
      background: color-mix(in srgb, var(--primary) 85%, #000);
    }
  `
      : `
    background: #fff;
    color: rgba(0, 0, 0, 0.65);
    border: 1px solid #d9d9d9;

    &:hover:not(:disabled) {
      border-color: var(--primary);
      color: var(--primary);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
`;

type Props = {
  isVisible: boolean;
  closeModal: () => void;
  data: User | null;
  setUsers: Dispatch<SetStateAction<InvitedUser[]>>;
};

type FormProps = {
  email: string;
};

export const UserInviteModal = ({
  isVisible,
  closeModal,
  data,
  setUsers,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const { useForm } = Form;
  const [form] = useForm<FormProps>();

  async function sendInviteToUser() {
    const { email } = await form.validateFields();

    setIsLoading(true);
    try {
      const { invited_user } = await sendInvite(email);

      setUsers((users) => [...users, invited_user]);
      form.resetFields();
      closeModal();
    } finally {
      setIsLoading(false);
    }
  }

  function handleClose() {
    form.resetFields();
    closeModal();
  }

  return (
    <Modal
      title={null}
      open={isVisible}
      onCancel={handleClose}
      footer={null}
      width={420}
      destroyOnClose
    >
      <ModalContent>
        <IconWrapper>
          <HiOutlineUserAdd />
        </IconWrapper>

        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px 0', color: 'rgba(0, 0, 0, 0.85)' }}>
          Convidar usuário
        </h3>

        <Description>
          Um e-mail será enviado com instruções para o usuário criar sua conta e acessar o painel.
        </Description>

        <Form name="invite-user" layout="vertical" form={form} requiredMark={false}>
          <Form.Item
            label="E-mail do usuário"
            name="email"
            rules={inviteUserRules.email}
            style={{ marginBottom: 0 }}
          >
            <InputWrapper>
              <span className="input-icon">
                <HiOutlineMail />
              </span>
              <Input placeholder="exemplo@email.com" />
            </InputWrapper>
          </Form.Item>
        </Form>

        <FooterWrapper>
          <FooterButton type="button" onClick={handleClose}>
            Cancelar
          </FooterButton>
          <FooterButton
            type="button"
            $primary
            onClick={sendInviteToUser}
            disabled={isLoading}
          >
            {isLoading ? 'Enviando...' : 'Enviar convite'}
          </FooterButton>
        </FooterWrapper>
      </ModalContent>
    </Modal>
  );
};
